import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Ticket as TicketIcon } from 'lucide-react';
import type { Alert, HealthState, Ticket } from '@itr/shared';
import { formatRelative } from '@itr/shared';
import { api } from '../api/client';
import { movesHealthRollup, useRealtimeEvent, useThrottledReload } from '../api/realtime';
import { Badge, EmptyState, ErrorState, Health, Skeleton, useFetch } from './ui';
import { TimeseriesChart } from './charts';

interface Overview {
  resourceCount: number;
  health: Record<HealthState, number>;
  alerts: { critical: number; warning: number; info: number; acknowledged: number };
  openAlerts: Alert[];
  tickets: { open: number; unassigned: number; breached: number; byPriority: Record<string, number>; byState: Record<string, number> };
  openTickets: Ticket[];
  uptimeNow: number;
  uptime24h: number;
  uptime7d: number;
  uptime30d: number;
  uptimeSeries: Array<{ ts: number; pct: number }>;
  alertSeries: Array<{ ts: number; count: number }>;
  windowHours: number;
}

const RANGES = [
  { hours: 24, label: '1d' },
  { hours: 168, label: '7d' },
  { hours: 720, label: '30d' },
];

/**
 * The full client/site dashboard: live health right now, plus the historical
 * trend behind it. Shared between the client and site detail pages — a site
 * is just a narrower resource scope, so one endpoint shape and one component
 * cover both.
 */
export function ScopeOverview({ endpoint }: { endpoint: string }) {
  const [hours, setHours] = useState(24);
  const { data, loading, error, reload } = useFetch(
    () => api.get<Overview>(endpoint, { hours }),
    [endpoint, hours],
    { refreshMs: 30_000 },
  );

  // Any health flip, alert, or ticket anywhere can affect this scope's
  // rollup — cheaper to refetch the summary than to track which of this
  // client's resource ids the event belongs to. Routine readings are skipped
  // and bursts coalesced: the rollup is expensive, and the 30s poll keeps
  // the uptime figures fresh anyway.
  const throttledReload = useThrottledReload(reload, 5000);
  useRealtimeEvent(['resources', 'alerts', 'tickets'], (event) => {
    if (movesHealthRollup(event) || event.type.startsWith('alert.') || event.type.startsWith('ticket.')) throttledReload();
  });

  if (loading && !data) {
    return (
      <div className="card" style={{ marginBottom: 14, padding: 16 }}>
        <Skeleton height={90} style={{ marginBottom: 12 }} />
        <Skeleton height={160} />
      </div>
    );
  }
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return null;

  const downCount = data.health.down ?? 0;
  const degradedCount = data.health.degraded ?? 0;
  const openAlertCount = data.alerts.critical + data.alerts.warning + data.alerts.info;

  return (
    <>
      {(downCount > 0 || degradedCount > 0) && (
        <div
          className="card"
          style={{ padding: '10px 14px', marginBottom: 14, fontSize: 13, borderLeft: '3px solid var(--itr-warning)' }}
        >
          {downCount > 0 && `${downCount} resource${downCount === 1 ? '' : 's'} down`}
          {downCount > 0 && degradedCount > 0 && ' · '}
          {degradedCount > 0 && `${degradedCount} degraded`}
          {' — see the resource table below for which, and why.'}
        </div>
      )}

      <div className="stat-strip" style={{ marginBottom: 14 }}>
        <div className="stat-chip">
          <div className="stat-chip-label">Resources</div>
          <div className="stat-chip-value">{data.resourceCount}</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Up now</div>
          <div className="stat-chip-value" style={{ color: downCount || degradedCount ? 'var(--itr-warning)' : undefined }}>
            {data.health.up ?? 0}/{data.resourceCount}
          </div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Uptime 24h</div>
          <div className="stat-chip-value" style={{ color: data.uptime24h < 99 ? 'var(--itr-warning)' : undefined }}>
            {data.uptime24h.toFixed(2)}%
          </div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Uptime 7d</div>
          <div className="stat-chip-value" style={{ color: data.uptime7d < 99 ? 'var(--itr-warning)' : undefined }}>
            {data.uptime7d.toFixed(2)}%
          </div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Open alerts</div>
          <div className="stat-chip-value" style={{ color: openAlertCount ? 'var(--itr-critical)' : undefined }}>
            {openAlertCount}
          </div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Open tickets</div>
          <div className="stat-chip-value" style={{ color: data.tickets.breached ? 'var(--itr-critical)' : undefined }}>
            {data.tickets.open}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <span className="card-title">History</span>
          <span className="grow" />
          <div className="row" style={{ gap: 3 }}>
            {RANGES.map((r) => (
              <button
                key={r.hours}
                className={`btn btn-sm${hours === r.hours ? ' btn-primary' : ''}`}
                onClick={() => setHours(r.hours)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
          <div>
            <div className="row" style={{ marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 590, color: 'var(--itr-text-muted)' }}>Availability</span>
            </div>
            <div style={{ height: 150 }}>
              {data.uptimeSeries.length > 0 ? (
                <TimeseriesChart
                  series={[{ name: 'Uptime', points: data.uptimeSeries.map((p) => [p.ts, p.pct]), intent: 'good' }]}
                  unit="percent"
                  height={150}
                />
              ) : (
                <div className="subtle" style={{ fontSize: 12, paddingTop: 20 }}>
                  No samples in this window.
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="row" style={{ marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 590, color: 'var(--itr-text-muted)' }}>Alerts opened</span>
            </div>
            <div style={{ height: 150 }}>
              {data.alertSeries.some((p) => p.count > 0) ? (
                <TimeseriesChart
                  series={[{ name: 'Alerts', points: data.alertSeries.map((p) => [p.ts, p.count]), intent: 'bad' }]}
                  height={150}
                />
              ) : (
                <div className="subtle" style={{ fontSize: 12, paddingTop: 20 }}>
                  No alerts in this window.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {(data.openAlerts.length > 0 || data.openTickets.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, marginBottom: 14 }}>
          {data.openAlerts.length > 0 && (
            <div className="card">
              <div className="card-header">
                <Activity size={14} style={{ color: 'var(--itr-critical)' }} />
                <span className="card-title">Open alerts</span>
              </div>
              <div style={{ padding: '4px 0' }}>
                {data.openAlerts.slice(0, 5).map((alert) => (
                  <Link
                    key={alert.id}
                    to={`/alerts/${alert.id}`}
                    className="row"
                    style={{ padding: '9px 16px', gap: 10, color: 'inherit', textDecoration: 'none' }}
                  >
                    <Badge intent={alert.severity === 'critical' ? 'bad' : 'warn'}>{alert.severity}</Badge>
                    <span className="grow truncate">{alert.title}</span>
                    <span className="subtle" style={{ fontSize: 12 }}>
                      {formatRelative(alert.lastSeenAt)}
                    </span>
                  </Link>
                ))}
                {data.openAlerts.length > 5 && (
                  <Link to="/alerts" className="row" style={{ padding: '9px 16px', fontSize: 12 }}>
                    View all {data.openAlerts.length} open alerts
                  </Link>
                )}
              </div>
            </div>
          )}

          {data.openTickets.length > 0 && (
            <div className="card">
              <div className="card-header">
                <TicketIcon size={14} />
                <span className="card-title">Open tickets</span>
              </div>
              <div style={{ padding: '4px 0' }}>
                {data.openTickets.map((ticket) => (
                  <Link
                    key={ticket.id}
                    to={`/tickets/${ticket.id}`}
                    className="row"
                    style={{ padding: '9px 16px', gap: 10, color: 'inherit', textDecoration: 'none' }}
                  >
                    <Badge intent={ticket.priority === 'urgent' || ticket.priority === 'high' ? 'bad' : 'neutral'}>
                      {ticket.priority}
                    </Badge>
                    <span className="grow truncate">{ticket.subject}</span>
                    <span className="subtle" style={{ fontSize: 12 }}>
                      {formatRelative(ticket.updatedAt)}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {data.resourceCount === 0 && (
        <EmptyState title="Nothing to track yet" text="Attach resources to see live health and history here." />
      )}
    </>
  );
}
